import { type ParsedLocation, useRouter } from '@tanstack/react-router';
import { t } from 'i18next';
import { Layers, LayoutGrid, type LucideIcon, Rocket } from 'lucide-react';

import { useClusterCategories, useOpenShelves } from '@/api/generated';

export interface NavItem {
  title: string;
  description?: string;
  location: ParsedLocation;
  items?: Array<NavItem>;
  icon?: LucideIcon;
  // Names the tab for UI tests: data-testid="topbar-tab-<testId>".
  testId?: string;
  // Work still waiting, shown next to the title when above zero.
  count?: number;
}

/**
 * All application configuration should be stored/handled here.
 * Purpose of this hook: if a new project is setup using the kickstart template, you only need to modify this hook to change the application configuration.
 */
export function useAppConfig(): {
  name: string;
  logo: React.ReactNode;
  items: Array<NavItem>;
} {
  const router = useRouter();
  const { data: openShelves } = useOpenShelves();
  const { data: categories } = useClusterCategories();
  const clustersLeft = categories?.items.reduce(
    (sum, category) => sum + category.remaining,
    0
  );

  return {
    name: 'Labeling Tool',
    logo: <Rocket className='h-8 w-8 p-2' />,
    items: [
      {
        title: t('customers.navTitle'),
        icon: LayoutGrid,
        testId: 'shelves',
        count: openShelves?.count,
        location: router.buildLocation({
          to: '/app/shelves',
        }),
      },
      {
        title: t('clusters.title'),
        icon: Layers,
        testId: 'clusters',
        count: clustersLeft,
        location: router.buildLocation({
          to: '/app/clusters',
        }),
      },
    ],
  };
}
